import React from 'react';
import { Check, CheckCheck, Calendar, FileText, Image as ImageIcon, LucideImage, Trash2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from '@/context/auth';
import { format } from 'date-fns';

interface MessageItemProps {
  senderId: string;
  content: string;
  timestamp: string;
  isDelivered: boolean;
  isRead: boolean;
  isBookingRequest?: boolean;
  contentType?: string;
  attachmentUrl?: string | null;
  attachmentType?: string | null;
  senderAvatar?: string | null;
  senderName?: string;
  onDelete?: () => void;
  messageId?: string;
}

const MessageItem: React.FC<MessageItemProps> = ({
  senderId,
  content,
  timestamp,
  isDelivered,
  isRead,
  isBookingRequest = false,
  contentType = 'text',
  attachmentUrl,
  attachmentType,
  senderAvatar,
  senderName,
  onDelete,
  messageId,
}) => {
  const { user } = useAuth();
  const isCurrentUser = senderId === user?.id;

  // Render attachment based on type
  const renderAttachment = () => {
    if (!attachmentUrl) return null;
    
    switch (attachmentType) {
      case 'image':
        return (
          <div className="rounded-md overflow-hidden mt-2 max-w-xs">
            <img 
              src={attachmentUrl} 
              alt="Attachment" 
              className="w-full h-auto object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).src = '/placeholder.svg';
              }}
            />
          </div>
        );
      case 'document':
        return (
          <a 
            href={attachmentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 mt-2 p-2 bg-secondary/50 rounded-md hover:bg-secondary"
          >
            <FileText size={20} />
            <span className="text-sm truncate">Document</span>
          </a>
        );
      default:
        return (
          <a 
            href={attachmentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-500 hover:underline mt-2 block"
          >
            View attachment
          </a>
        );
    }
  };

  const renderBookingRequestContent = () => {
    const bookingText = content.replace('Demande de rendez-vous pour', '');
    
    return (
      <div>
        <div className="flex items-center mb-1 text-pink-500 dark:text-pink-300 font-semibold">
          <Calendar size={16} className="mr-1" />
          <span>Demande de rendez-vous</span>
        </div>
        <p>
          <span className="font-medium">Pour</span>
          {bookingText}
        </p>
      </div>
    );
  };

  return (
    <div className={`flex mb-4 group ${isCurrentUser ? 'justify-end' : 'justify-start'}`}>
      {!isCurrentUser && (
        <Avatar className="h-8 w-8 mr-2 mt-1">
          <AvatarImage src={senderAvatar || "/placeholder-user.png"} alt={senderName || "User"} />
          <AvatarFallback>{senderName?.substring(0, 2) || 'U'}</AvatarFallback>
        </Avatar>
      )}
      
      <div className={`max-w-[75%] relative`}>
        {isCurrentUser && onDelete && (
          <button
            onClick={onDelete}
            className="absolute -left-7 top-2 text-gray-400 hover:text-pink-600 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Supprimer ce message"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
        <div className={`px-4 py-2 rounded-2xl ${
          isCurrentUser 
            ? 'bg-primary text-primary-foreground rounded-tr-none' 
            : 'bg-secondary text-secondary-foreground rounded-tl-none'
        } ${isBookingRequest ? 'border-2 border-pink-400' : ''}`}>
          {isBookingRequest ? (
            renderBookingRequestContent()
          ) : contentType === 'image' ? (
            <div>
              {content && <p className="mb-2">{content}</p>}
              <div className="rounded-md overflow-hidden">
                <img 
                  src={attachmentUrl || ''} 
                  alt="Image" 
                  className="w-full h-auto max-h-[200px] object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/placeholder.svg';
                  }}
                />
              </div>
            </div>
          ) : (
            <>
              <p>{content}</p>
              {renderAttachment()}
            </>
          )}
        </div>
        
        <div className="flex items-center mt-1 text-xs text-muted-foreground">
          <span>{timestamp}</span>
          {isCurrentUser && (
            <span className="ml-1">
              {isRead ? (
                <CheckCheck size={12} className="text-green-500" />
              ) : isDelivered ? (
                <Check size={12} />
              ) : null}
            </span>
          )}
        </div>
      </div>
      
      {isCurrentUser && (
        <Avatar className="h-8 w-8 ml-2 mt-1">
          <AvatarImage 
            src={user?.subscriptionTier ? `/avatars/${user.id}.jpg` : "/placeholder-user.png"} 
            alt="You" 
          />
          <AvatarFallback>
            {user?.email ? user.email.substring(0, 2).toUpperCase() : 'U'}
          </AvatarFallback>
        </Avatar>
      )}
    </div>
  );
};

export default MessageItem;
